import { useCallback, useEffect, useState } from 'react';

interface UseNodeAttributeValueArgs<T> {
  value: T;
  onChange: (value: T) => void;
}

/**
 * Mirrors a node attribute in local state for the controls of a custom editor block.
 *
 * Since Tiptap 3.28 a node view re-renders one microtask after `updateAttributes`, so a
 * control that reads `node.attrs` directly gets its new value too late: text fields lose
 * the caret position and dnd-kit animates reordered items from stale positions.
 * The local copy changes in the same render as the user's action, the attribute is still
 * written through `onChange`, and changes made elsewhere (undo, uploads) are synced back.
 *
 * Use it for any typed field or sortable list in a custom block that is bound to an attribute.
 */
export const useNodeAttributeValue = <T>({ value, onChange }: UseNodeAttributeValueArgs<T>) => {
  const [attributeValue, setAttributeValue] = useState<T>(value);

  useEffect(() => {
    setAttributeValue(value);
  }, [value]);

  const changeHandler = useCallback(
    (nextValue: T) => {
      setAttributeValue(nextValue);
      onChange(nextValue);
    },
    [onChange]
  );

  return { value: attributeValue, changeHandler };
};
